// A tap on the microphone stream that yields raw 16 kHz mono PCM.
//
// The real-time transport has to send audio while the learner is still
// speaking, and vad-web 0.0.16 keeps the frames it captures to itself — its
// only output is the finished utterance handed to onSpeechEnd. So this opens
// its own AudioContext at 16 kHz, runs the stream through a ScriptProcessorNode
// and hands whole frames of little-endian Int16 to a callback.
//
// It also keeps every chunk in memory. When the socket path fails the learner
// must not lose the utterance, so the same samples become a WAV and go up the
// existing POST path instead.
//
// The context is the reason stop() must always be called: browsers cap how many
// AudioContexts a page may open (LAN-381).

export const REALTIME_SAMPLE_RATE = 16000;

// 1024 samples is 64 ms at 16 kHz. A ScriptProcessorNode only ever delivers
// whole blocks, so the block size is also the largest tail stop() can lose: at
// 4096 a one-word answer could lose a quarter of a second of itself. Four
// blocks then go out as one socket frame, so the wire keeps the cadence the
// contract asks for (100–250 ms, never above 64 KB) without paying for it in
// dropped tail.
const BUFFER_SIZE = 1024;
const BLOCKS_PER_FRAME = 4;
const WAV_HEADER_BYTES = 44;

// Leading and trailing silence is a hallucination trigger for the async
// fallback, and it is pure weight on the upload. 300 ms stays either side so a
// quiet first syllable is never clipped off.
export const SILENCE_RMS_THRESHOLD = 300; // about -40 dBFS on the 16-bit scale
const SILENCE_PADDING_MS = 300;

export function floatTo16BitPcm(input) {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[i]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
  }
  return output;
}

// Safari hands back a context at its own rate whatever sampleRate was asked
// for, and the wire and the WAV are both 16 kHz. Linear interpolation is
// enough for speech at these ratios; the phase resets on each block, which
// costs a sample of error at the seam and nothing audible.
export function downsampleFloat(input, inputRate, outputRate) {
  if (!inputRate || inputRate === outputRate) return input;
  const ratio = inputRate / outputRate;
  const length = Math.floor(input.length / ratio);
  if (length <= 0) return new Float32Array(0);

  const output = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const position = i * ratio;
    const index = Math.floor(position);
    const next = Math.min(index + 1, input.length - 1);
    const weight = position - index;
    output[i] = input[index] * (1 - weight) + input[next] * weight;
  }
  return output;
}

export function int16SampleCount(chunks) {
  return (chunks || []).reduce((sum, chunk) => sum + chunk.length, 0);
}

function concatInt16(chunks) {
  if (chunks.length === 1) return chunks[0];
  const output = new Int16Array(int16SampleCount(chunks));
  let offset = 0;
  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });
  return output;
}

function blockRms(chunk) {
  if (!chunk || chunk.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < chunk.length; i += 1) {
    sum += chunk[i] * chunk[i];
  }
  return Math.sqrt(sum / chunk.length);
}

function firstBlockAfterPadding(chunks, index, padding) {
  let remaining = padding;
  let i = index;
  while (i > 0 && remaining > 0) {
    i -= 1;
    remaining -= chunks[i].length;
  }
  return i;
}

function lastBlockAfterPadding(chunks, index, padding) {
  let remaining = padding;
  let i = index;
  while (i < chunks.length - 1 && remaining > 0) {
    i += 1;
    remaining -= chunks[i].length;
  }
  return i;
}

// Drops the silent blocks at each end of a captured utterance. Block
// granularity is deliberate: it is what the processor delivers, and a whole
// block is 64 ms, well below the padding.
export function trimSilentEdges(chunks, sampleRate = REALTIME_SAMPLE_RATE, threshold = SILENCE_RMS_THRESHOLD) {
  if (!chunks || chunks.length === 0) return [];

  const loud = chunks.map((chunk) => blockRms(chunk) >= threshold);
  const first = loud.indexOf(true);
  // Nothing anywhere clears the threshold. That may be a whisper rather than
  // silence, and losing an utterance is the one thing this branch must never
  // do, so keep every sample.
  if (first === -1) return chunks;

  const last = loud.lastIndexOf(true);
  const padding = Math.round((SILENCE_PADDING_MS / 1000) * sampleRate);
  return chunks.slice(
    firstBlockAfterPadding(chunks, first, padding),
    lastBlockAfterPadding(chunks, last, padding) + 1
  );
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i += 1) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

// The fallback encoder. audioBufferToWavBlob() on the recording service does
// the same job for an AudioBuffer; the realtime path never builds one, it only
// ever holds Int16 chunks.
export function int16ChunksToWavBlob(chunks, sampleRate = REALTIME_SAMPLE_RATE) {
  const samples = int16SampleCount(chunks);
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + samples * 2);
  const view = new DataView(buffer);
  const numChannels = 1;

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples * 2, true);

  let offset = WAV_HEADER_BYTES;
  chunks.forEach((chunk) => {
    for (let i = 0; i < chunk.length; i += 1, offset += 2) {
      view.setInt16(offset, chunk[i], true);
    }
  });

  return new Blob([view], { type: 'audio/wav' });
}

class PcmStreamTap {
  constructor({ stream, sampleRate = REALTIME_SAMPLE_RATE, onChunk = null } = {}) {
    this.stream = stream;
    this.sampleRate = sampleRate;
    this.blocksPerFrame = BLOCKS_PER_FRAME;
    this.onChunk = onChunk;
    this.audioContext = null;
    // The rate the context actually runs at, which is not always the rate we
    // asked for.
    this.contextSampleRate = sampleRate;
    this.source = null;
    this.processor = null;
    this.gain = null;
    this.chunks = [];
    // The blocks captured since the last socket frame went out.
    this.pending = [];
    this.started = false;
  }

  // All or nothing: a context that is built and then fails to wire up is
  // closed here, because browsers cap how many a page may hold open (LAN-381)
  // and the caller's fallback path has no reference to it.
  start() {
    if (this.started) return;

    let audioContext = null;
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: this.sampleRate });
      this.contextSampleRate = audioContext.sampleRate || this.sampleRate;
      // One socket frame stays about the same span of audio whatever rate the
      // context settled on.
      this.blocksPerFrame = Math.max(
        1,
        Math.round(BLOCKS_PER_FRAME * (this.contextSampleRate / this.sampleRate))
      );

      const source = audioContext.createMediaStreamSource(this.stream);
      const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      processor.onaudioprocess = (event) => { this.handleAudio(event); };
      source.connect(processor);

      // A ScriptProcessorNode only runs while something pulls its output, and
      // that output is what reaches the speakers — it is silent only because
      // nothing writes the output buffer. A gain of zero makes that explicit
      // instead of relying on it.
      const gain = audioContext.createGain();
      if (gain && gain.gain) gain.gain.value = 0;
      processor.connect(gain);
      gain.connect(audioContext.destination);

      this.audioContext = audioContext;
      this.source = source;
      this.processor = processor;
      this.gain = gain;
      this.started = true;
    } catch (error) {
      this.discardContext(audioContext);
      throw error;
    }

    // Autoplay policy and a backgrounded tab both hand back a suspended
    // context, which captures silence until it resumes.
    this.resumeContext();
  }

  discardContext(audioContext) {
    this.audioContext = null;
    this.source = null;
    this.processor = null;
    this.gain = null;
    this.started = false;
    if (!audioContext || typeof audioContext.close !== 'function') return;
    if (audioContext.state === 'closed') return;
    try {
      const closing = audioContext.close();
      if (closing && typeof closing.catch === 'function') closing.catch(() => {});
    } catch (error) {
      /* nothing left to do with it */
    }
  }

  resumeContext() {
    const audioContext = this.audioContext;
    if (!audioContext || typeof audioContext.resume !== 'function') return;
    if (audioContext.state !== 'suspended') return;
    try {
      const resuming = audioContext.resume();
      if (resuming && typeof resuming.catch === 'function') {
        resuming.catch((error) => { console.error('Error resuming PCM tap audio context:', error); });
      }
    } catch (error) {
      console.error('Error resuming PCM tap audio context:', error);
    }
  }

  handleAudio(event) {
    if (!this.started) return;
    const input = event.inputBuffer.getChannelData(0);
    const resampled = downsampleFloat(input, this.contextSampleRate, this.sampleRate);
    if (resampled.length === 0) return;

    const chunk = floatTo16BitPcm(resampled);
    this.chunks.push(chunk);
    this.pending.push(chunk);
    if (this.pending.length < this.blocksPerFrame) return;

    const frame = this.flushPending();
    if (frame && this.onChunk) this.onChunk(frame);
  }

  // Hands back the batched blocks as one frame and forgets them.
  flushPending() {
    if (this.pending.length === 0) return null;
    const pending = this.pending;
    this.pending = [];
    return concatInt16(pending);
  }

  // Hands the caller everything captured so far and forgets it, so a fallback
  // upload and a later session cannot share samples.
  takeBuffer() {
    const chunks = this.chunks;
    this.chunks = [];
    this.pending = [];
    return chunks;
  }

  bufferedSampleCount() {
    return int16SampleCount(this.chunks);
  }

  bufferedWavBlob() {
    return int16ChunksToWavBlob(this.chunks, this.sampleRate);
  }

  // Returns the tail: the blocks captured since the last frame went out. The
  // caller must put it on the socket before it sends `stop`, otherwise the end
  // of the utterance is transcribed from nothing.
  async stop() {
    this.started = false;
    const tail = this.flushPending();

    if (this.processor) {
      this.processor.onaudioprocess = null;
      try { this.processor.disconnect(); } catch (error) { /* already detached */ }
      this.processor = null;
    }
    if (this.gain) {
      try { this.gain.disconnect(); } catch (error) { /* already detached */ }
      this.gain = null;
    }
    if (this.source) {
      try { this.source.disconnect(); } catch (error) { /* already detached */ }
      this.source = null;
    }

    const audioContext = this.audioContext;
    this.audioContext = null;
    if (audioContext && typeof audioContext.close === 'function' && audioContext.state !== 'closed') {
      try {
        await audioContext.close();
      } catch (error) {
        console.error('Error closing PCM tap audio context:', error);
      }
    }

    return tail;
  }
}

export default PcmStreamTap;
